/**
 * AI polish pass for player briefs.
 *
 * Takes the rule-generated `PlayerBrief[]` from
 * `@4cm/core/tactics/playerBriefs` and runs each through Claude
 * (Haiku tier — see `aiSurfaces.player-briefs`) to humanise the prose.
 * Per spec §14.2, every failure falls back silently to the rule
 * output — the polish pass is strictly additive.
 *
 * Concurrency: briefs are polished in parallel (Promise.allSettled) so
 * a regen-all-eleven completes in roughly one round-trip rather than
 * eleven. The Anthropic proxy holds the rate limit.
 */

import { callAI } from '@4cm/core/ai';
import type { PlayerBrief, TacticsLayout } from '@4cm/core/tactics';
import { isTeamTacticsBundle } from '@4cm/core/tactics/teamTactics';
import {
  PLAYER_BRIEF_POLISH_SYSTEM,
  buildPlayerBriefPolishUserContent,
  parsePolishResponse,
  type PlayerBriefPolishContext,
} from './prompts/playerBrief';

/** Compose the tactical context summary surfaced to the polish prompt. */
function buildContext(
  layout: TacticsLayout,
  side: 'home' | 'away',
): PlayerBriefPolishContext {
  const bundle = layout.teamTactics;
  let summary = 'Balanced build-up, mid-block.';
  if (bundle && isTeamTacticsBundle(bundle)) {
    const sideBundle = bundle[side];
    const active = sideBundle.slots.find((s) => s.id === sideBundle.activeSlotId);
    if (active) {
      const cfg = active.tactics;
      const sliderBits = Object.entries(cfg.sliders)
        .filter(([, v]) => v >= 70 || v <= 30)
        .map(([k, v]) => `${k} ${v}`)
        .join(', ');
      summary = `${cfg.buildUpStyle} build-up, ${cfg.defensiveApproach}${
        sliderBits ? ` (${sliderBits})` : ''
      }`;
    }
  }
  return {
    formation: side === 'home' ? layout.homeFormation : layout.awayFormation,
    oppFormation: side === 'home' ? layout.awayFormation : layout.homeFormation,
    teamTacticsSummary: summary,
  };
}

/**
 * Polish a single rule-generated brief. Returns a brief with
 * `source: 'ai'` on success, or the unchanged original (with
 * `source: 'rules'`) on any failure.
 */
export async function polishPlayerBrief(
  brief: PlayerBrief,
  ctx: PlayerBriefPolishContext,
  opts: { signal?: AbortSignal } = {},
): Promise<PlayerBrief> {
  const userContent = buildPlayerBriefPolishUserContent(brief, ctx);
  let text: string | null = null;
  try {
    text = await callAI({
      system: PLAYER_BRIEF_POLISH_SYSTEM,
      userContent,
      surface: 'player-briefs',
      maxTokens: 500,
      signal: opts.signal,
    });
  } catch {
    return brief;
  }
  if (!text) return brief;
  const parsed = parsePolishResponse(text);
  if (!parsed) return brief;
  // Section-count preservation per the prompt — if Claude returns
  // fewer/more bullets in any section than the rule brief, fall back.
  if (
    parsed.inPossession.length !== brief.inPossession.length ||
    parsed.outOfPossession.length !== brief.outOfPossession.length ||
    parsed.transitions.length !== brief.transitions.length ||
    parsed.setPieces.length !== brief.setPieces.length
  ) {
    return brief;
  }
  return {
    ...brief,
    inPossession: parsed.inPossession,
    outOfPossession: parsed.outOfPossession,
    transitions: parsed.transitions,
    setPieces: parsed.setPieces,
    source: 'ai',
    generatedAt: new Date().toISOString(),
  };
}

/**
 * Polish all briefs in parallel. Order is preserved. Any individual
 * brief that fails to polish stays in its rule form.
 */
export async function polishAllPlayerBriefs(
  briefs: PlayerBrief[],
  layout: TacticsLayout,
  side: 'home' | 'away',
  opts: { signal?: AbortSignal } = {},
): Promise<PlayerBrief[]> {
  const ctx = buildContext(layout, side);
  const results = await Promise.allSettled(
    briefs.map((b) => polishPlayerBrief(b, ctx, opts)),
  );
  return results.map((r, i) =>
    r.status === 'fulfilled' ? r.value : (briefs[i] as PlayerBrief),
  );
}
